import { LiveChannelScope, isLiveChannelMessageEvent } from '@grafana/data';
import { getBackendSrv, getGrafanaLiveSrv, logDebug } from '@grafana/runtime';
import { pipe } from 'rxjs';
import { filter, map, scan, tap, takeWhile } from 'rxjs/operators';
import { LLM_PLUGIN_ID, LLM_PLUGIN_ROUTE, setLLMPluginVersion } from './constants.js';

const OPENAI_CHAT_COMPLETIONS_PATH = "openai/v1/chat/completions";
function isContentMessage(message) {
  return message.content != null;
}
function isDoneMessage(message) {
  return message.done !== void 0;
}
function isErrorResponse(response) {
  return response.error !== void 0;
}
function extractContent() {
  return pipe(
    filter((response) => isContentMessage(response.choices[0].delta)),
    // The type assertion is needed here because the type predicate above doesn't seem to propagate.
    map((response) => response.choices[0].delta.content)
  );
}
function accumulateContent() {
  return pipe(
    extractContent(),
    scan((acc, curr) => acc + curr, "")
  );
}
async function chatCompletions(request) {
  const response = await getBackendSrv().post("/api/plugins/grafana-llm-app/resources/openai/v1/chat/completions", request, {
    headers: { "Content-Type": "application/json" }
  });
  return response;
}
function streamChatCompletions(request) {
  const channel = {
    scope: LiveChannelScope.Plugin,
    namespace: LLM_PLUGIN_ID,
    path: OPENAI_CHAT_COMPLETIONS_PATH + "/" + self.crypto.randomUUID(),
    data: request
  };
  const messages = getGrafanaLiveSrv().getStream(channel).pipe(filter((event) => isLiveChannelMessageEvent(event)));
  return messages.pipe(
    tap((event) => {
      if (isErrorResponse(event.message)) {
        throw new Error(event.message.error);
      }
    }),
    takeWhile((event) => isErrorResponse(event.message) || !isDoneMessage(event.message.choices[0].delta)),
    map((event) => event.message)
  );
}
let loggedWarning = false;
const enabled = async () => {
  try {
    const settings = await getBackendSrv().get(`${LLM_PLUGIN_ROUTE}/settings`, void 0, void 0, {
      showSuccessAlert: false,
      showErrorAlert: false
    });
    if (!settings.enabled) {
      return { configured: false, ok: false, error: "The Grafana LLM plugin is not enabled." };
    }
  } catch (e) {
    logDebug(String(e));
    logDebug("Failed to check if OpenAI is enabled. This is expected if the Grafana LLM plugin is not installed, and the above error can be ignored.");
    loggedWarning = true;
    return { configured: false, ok: false, error: "The Grafana LLM plugin is not installed." };
  }
  let response;
  try {
    response = await getBackendSrv().get(`${LLM_PLUGIN_ROUTE}/health`, void 0, void 0, {
      showSuccessAlert: false,
      showErrorAlert: false
    });
  } catch (e) {
    if (!loggedWarning) {
      logDebug(String(e));
      logDebug("Failed to check if OpenAI is enabled. This is expected if the Grafana LLM plugin is not installed, and the above error can be ignored.");
      loggedWarning = true;
    }
    return { configured: false, ok: false, error: "The Grafana LLM plugin is not installed." };
  }
  const { details } = response;
  if ((details == null ? void 0 : details.version) !== void 0) {
    setLLMPluginVersion(details.version);
  }
  if ((details == null ? void 0 : details.openAI) === void 0) {
    return { configured: false, ok: false, error: "The Grafana LLM plugin is outdated; please update it." };
  }
  return typeof details.openAI === "boolean" ? { configured: details.openAI, ok: details.openAI } : details.openAI;
};

export { accumulateContent, chatCompletions, enabled, extractContent, isContentMessage, isDoneMessage, isErrorResponse, streamChatCompletions };
//# sourceMappingURL=openai.js.map
